import { MouseEvent } from 'react';
import { Box, ProfileImage, ProfileInitials } from '@hopskipdrive/react-components';
import Checkbox from '@mui/material/Checkbox';
import { RiderServices, SERVICE_ICON_CODES } from '@rideiq/riders/features/RiderServices';
import { Text } from '@rideiq/shared/components/Text';
import { SortableColumnHeader, SortState } from '@rideiq/shared/sortFilter';
import { formatPhone } from '@rideiq/utils/helpers';

import { PrimaryCaregiver, RiderName, SafetySettings } from './RidersTable.styles';

const RiderNameCell = ({ rowData }: { rowData: RiderForList }) => (
  <Box display="flex" alignItems="center">
    {rowData.profilePhotoUrl ? (
      <span>
        <ProfileImage key={rowData.fullName} imgSrc={rowData.profilePhotoUrl} />
      </span>
    ) : (
      <ProfileInitials key={rowData.fullName} fullName={rowData.fullName} />
    )}
    <RiderName>{rowData.fullName}</RiderName>
  </Box>
);

const SafetySettingsCell = ({ rowData }: { rowData: RiderForList }) => {
  const { frontSeatAuthorized, boosterSeat, mustBeMet } = rowData.safetySettings;
  const hasAny = frontSeatAuthorized || boosterSeat || mustBeMet;

  return (
    <SafetySettings aria-label={hasAny ? undefined : 'Safety Settings Empty'}>
      <span>{frontSeatAuthorized ? 'Can Sit Up Front' : null}</span>
      <span>{boosterSeat ? 'Booster Seat Required' : null}</span>
      <span>{mustBeMet ? 'Must Be Met' : null}</span>
    </SafetySettings>
  );
};

const RiderServicesCell = ({ rowData }: { rowData: RiderForList }) => {
  const services = (rowData.services ?? []).filter((service) => SERVICE_ICON_CODES.includes(service.code));

  if (services.length === 0) {
    return <span aria-label="Services Empty" />;
  }

  return <RiderServices services={services} />;
};

const PrimaryCaregiverCell = ({ rowData }: { rowData: RiderForList }) =>
  rowData.primaryCaregiver ? (
    <PrimaryCaregiver aria-label={rowData.primaryCaregiver.phoneNumber ? undefined : 'Primary Caregiver Empty'}>
      <Text>{formatPhone(rowData.primaryCaregiver.phoneNumber)}</Text>
      {rowData.primaryCaregiver.fullName && (
        <Text ml={2} textColor="secondaryText">
          {`(${rowData.primaryCaregiver.fullName})`}
        </Text>
      )}
    </PrimaryCaregiver>
  ) : null;

export const invoiceGroupColumns = [
  {
    key: 'name',
    sortable: true,
    label: 'Name',
    align: 'left' as const,
    valueGetter: (rowData: RiderForList) => <RiderNameCell rowData={rowData} />,
  },
  {
    key: 'age',
    sortable: true,
    label: 'Age',
    align: 'center' as const,
  },
  {
    label: 'Safety Settings',
    align: 'left' as const,
    valueGetter: (rowData: RiderForList) => <SafetySettingsCell rowData={rowData} />,
  },
  {
    key: 'category',
    label: 'Category',
    align: 'left' as const,
    valueSetter: (category) => (
      <span aria-label={category ? undefined : 'Category Empty'}>
        {category === 'IEP' ? 'IEP / 504 / Special Needs' : category || ''}
      </span>
    ),
  },
  {
    label: 'Primary Caregiver',
    align: 'left' as const,
    valueGetter: (rowData: RiderForList) => <PrimaryCaregiverCell rowData={rowData} />,
  },
  {
    label: 'Invoice Group',
    align: 'left' as const,
    valueGetter: (rowData: RiderForList) => rowData.invoiceGroup?.name || '',
  },
  {
    label: 'Services',
    align: 'left' as const,
    valueGetter: (rowData: RiderForList) => <RiderServicesCell rowData={rowData} />,
  },
];

export const getSortableColumns = (sortState: SortState, onSort: (col: string) => void) =>
  invoiceGroupColumns.map((col) => {
    if (!col.sortable) {
      return col;
    }

    return {
      ...col,
      label: (
        <SortableColumnHeader
          column={{ key: col.key, label: col.label, sortable: true }}
          sortState={sortState}
          onSort={onSort}
        />
      ),
    };
  });

export const getShowCheckboxes = (
  isAdminOrOrganizerUser: boolean,
  currentStatusTab: string,
  enableBulkRiderStatusChange: boolean
) => {
  if (!isAdminOrOrganizerUser) {
    return false;
  }
  // Deactivated riders only get a bulk action (Reactivate) once the flag is on — no flag, no valid
  // action, so there's nothing checkboxes on that tab would let you do.
  if (currentStatusTab === 'deactivated') {
    return Boolean(enableBulkRiderStatusChange);
  }

  return ['active', 'paused'].includes(currentStatusTab);
};

const isAllPageSelected = (pageRiderIds: number[], selectedRiderIds: number[]) =>
  pageRiderIds.length > 0 && pageRiderIds.every((id) => selectedRiderIds.includes(id));

const isIndeterminate = (pageRiderIds: number[], selectedRiderIds: number[]) =>
  pageRiderIds.some((id) => selectedRiderIds.includes(id)) && !isAllPageSelected(pageRiderIds, selectedRiderIds);

export const getCheckboxColumn = (
  pageRiderIds: number[],
  selectedRiderIds: number[],
  handleSelectAllOnPage: (pageRiderIds: number[]) => void,
  handleCheckboxSelection: (event: MouseEvent, id: number, riderIds: number[]) => void
) => ({
  label: (
    <Checkbox
      size="small"
      checked={pageRiderIds.length > 0 && isAllPageSelected(pageRiderIds, selectedRiderIds)}
      indeterminate={isIndeterminate(pageRiderIds, selectedRiderIds)}
      onClick={(e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        handleSelectAllOnPage(pageRiderIds);
      }}
    />
  ),
  align: 'center' as const,
  width: '72px',
  valueGetter: (rowData: RiderForList) => (
    <Checkbox
      size="small"
      checked={selectedRiderIds.includes(rowData.id)}
      onClick={(e: MouseEvent<HTMLButtonElement>) =>
        handleCheckboxSelection(e as unknown as MouseEvent, rowData.id, pageRiderIds)
      }
    />
  ),
});
